import { withTimeout } from '@/lib/compat'
import type { ContentCard, Episode } from '@/lib/catalog'
import { withLanguage } from '@/lib/language'
import { readSelectedProfileId } from '@/lib/profiles'
import { authStatus, readSession } from '@/lib/session'

/**
 * «Davam et» — yarımçıq qalmış filmlər (`/api/me/watch-progress/continue`).
 *
 * ⚠ NİYƏ ƏLAVƏ OLUNDU (sahib tələbi 2026-08-03: «android tvdə var və digər
 *   tvlərdə də davam et section olsun»): Android TV və Apple TV bu sıranı
 *   `HomeViewModel`-də əvvəldən qururdu, veb TV isə YALNIZ redaksiya bloklarını
 *   və «Sizin üçün seçilənlər»i göstərirdi.
 *
 * ⚠ AKTİV BAXIŞ PROFİLİNƏ bağlıdır — `profileId` məcburi query parametridir
 *   (boş göndərsək 400 alardıq), ona görə profil seçilməyibsə sorğu ümumiyyətlə
 *   getmir.
 *
 * ⚠ HEÇ VAXT REJECT ETMİR: sıra boot yolundadır (`main.ts`), sorğu sınsa BÜTÜN
 *   ekran gecikərdi. Alınmasa boş massiv qayıdır və sıra sadəcə qurulmur.
 */

const TIMEOUT_MS = 6000

/** Kart + 0…1 aralığında baxılma nisbəti (kartın altındakı zolaq üçün). */
export interface ContinueCard {
  card: ContentCard
  progress: number
}

/** Serverin `ContinueWatchingItemDto` cavabı (TV-yə lazım olan sahələr). */
interface ContinueItem {
  contentId: string
  contentTitle: string | null
  slug: string | null
  posterUrl: string | null
  positionSeconds: number
  durationSeconds: number | null
  completed: boolean
  /** `wch.watch_progress` polimorf açarı: 1 = film, 2 = bölüm (`itemId` bölümün id-si). */
  itemType?: number | null
  itemId?: string | null
  lastWatchedAt?: string | null
  /**
   * ── Fokus qatının metası ──────────────────────────────────────────────────
   * ⚠ SERVERDƏ ARTIQ VAR idi (`ContinueWatchingItemDto` — MYTasks 500/512),
   *   burada sadəcə OXUNMURDU: sahib bildirdi ki, «davam et və sonra bax sadəcə
   *   film adı görünür» (2026-08-08). `durationMinutes` FİLMİN müddətidir,
   *   `durationSeconds` isə izlənən nüsxənin uzunluğu (progress zolağı üçün).
   * ⚠ Opsionaldır: köhnə API binarı bu sahələri göndərmir — kart yenə qurulur.
   */
  ageLimit?: number | null
  year?: number | null
  rating?: number | null
  durationMinutes?: number | null
  genreNames?: string[]
  moodNames?: string[]
}

/** `wch.watch_progress.item_type` — bölüm (`lib/playback.ts` → `ITEM_TYPE_EPISODE`). */
const ITEM_TYPE_EPISODE = 2

/** Serialda «İzlə»nin hansı bölümü açacağını seçmək üçün saxlanan sətir. */
interface ResumeEntry {
  contentId: string
  itemType: number
  itemId: string
  completed: boolean
}

/**
 * «DAVAM ET» KEŞİ — bölüm seçimi üçün (sayt `lib/seriesResume.ts` · iOS `SeriesResume.swift` ·
 * Android `SeriesResume.kt` ilə EYNİ qayda).
 *
 * ⚠⚠ 2026-10-25, kod oxunuşu: kart sırası qurularkən `itemType` · `itemId` · `completed` ·
 *    `lastWatchedAt` ATILIRDI (serialın bölümləri bir karta yığılır), «İzlə»/«Davam et» isə
 *    modalda, film səhifəsində və sonluq qatında həmişə `firstPlayableEpisode` çağırırdı.
 *    ƏVVƏL: S1B5-in ortasında qalan istifadəçi «Davam et» basanda 1-ci bölüm başlayırdı.
 *    İndi bütün sətirlər ən sondan əvvələ düzülüb saxlanılır; seçim `resumeEpisode`-dadır.
 * ⚠ SIRA MASSİVİN ÖZÜDÜR, VAXT MÜQAYİSƏSİ DEYİL: server sətirləri `lastWatchedAt` ilə qaytarır
 *   (yüklənəndə bir dəfə düzülür), bu TV-də yazılan mövqe isə BAŞA keçir (`noteWatchProgress`) —
 *   TV-nin saatı tez-tez səhv qurulur və yerli vaxt damğası server sətirlərini yanlış ötərdi.
 * ⚠ PROFİLƏ BAĞLIDIR: başqa profil seçiləndə keş oxunmur (yeni profilin sırası gələnə qədər
 *   birinci oynana bilən bölüm açılır).
 */
let resumeProfileId: string | null = null
let resumeEntries: ResumeEntry[] = []

/** Server vaxtı → ms. .NET 7 rəqəmli kəsri köhnə mühərrik oxumur — üç rəqəmə qədər kəsilir. */
function timeOf(raw: string | null | undefined): number {
  if (!raw) return 0
  let value = Date.parse(raw)
  if (isNaN(value)) value = Date.parse(raw.replace(/(\.\d{3})\d+/, '$1'))
  return isNaN(value) ? 0 : value
}

function rememberResume(profileId: string, items: ContinueItem[]): void {
  const rows: Array<{ entry: ResumeEntry; at: number; index: number }> = []
  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    if (!item || !item.contentId) continue
    rows.push({
      entry: {
        contentId: item.contentId,
        itemType: typeof item.itemType === 'number' ? item.itemType : 1,
        itemId: item.itemId || item.contentId,
        completed: item.completed === true,
      },
      at: timeOf(item.lastWatchedAt),
      index: i,
    })
  }
  // Ən son baxılan əvvəldə; vaxt oxunmasa serverin öz sırası (o da ən sondan düzür) qalır.
  rows.sort((a, b) => (b.at - a.at) || (a.index - b.index))
  resumeProfileId = profileId
  resumeEntries = rows.map((row) => row.entry)
}

/**
 * Bu TV-də yazılan mövqe keşə düşür (`lib/playback.ts` → `reportWatchProgress`) — serialın
 * 5-ci bölümünü yarımçıq qoyub film səhifəsinə qayıdan istifadəçinin «Davam et»i elə o
 * bölümü açsın, ana ekranın növbəti yüklənməsini gözləmədən.
 */
export function noteWatchProgress(
  profileId: string,
  contentId: string,
  episodeId: string | null | undefined,
  completed: boolean,
): void {
  if (resumeProfileId !== profileId) {
    resumeProfileId = profileId
    resumeEntries = []
  }
  const itemId = episodeId || contentId
  const next: ResumeEntry[] = [
    { contentId, itemType: episodeId ? ITEM_TYPE_EPISODE : 1, itemId, completed },
  ]
  for (let i = 0; i < resumeEntries.length; i++) {
    const entry = resumeEntries[i] as ResumeEntry
    if (entry.itemId !== itemId) next.push(entry)
  }
  resumeEntries = next
}

/**
 * Serialda «İzlə»/«Davam et» hansı bölümü açır: BU serialın ən son yarımçıq və oynana bilən
 * bölümü, yoxdursa birinci oynana bilən bölüm (`firstPlayableEpisode` ilə eyni). Mövqeyi bilet
 * özü gətirir (`startPositionSeconds`).
 */
export function resumeEpisode(contentId: string, episodes: Episode[]): Episode | null {
  let first: Episode | null = null
  const playable: Record<string, Episode> = {}
  for (let i = 0; i < episodes.length; i++) {
    const episode = episodes[i] as Episode
    if (!episode.hasVideo) continue
    if (!first) first = episode
    playable[episode.id] = episode
  }
  if (!first || resumeProfileId === null || resumeProfileId !== readSelectedProfileId()) return first
  for (let i = 0; i < resumeEntries.length; i++) {
    const entry = resumeEntries[i] as ResumeEntry
    if (entry.contentId !== contentId || entry.itemType !== ITEM_TYPE_EPISODE || entry.completed) continue
    const found = Object.prototype.hasOwnProperty.call(playable, entry.itemId) ? playable[entry.itemId] : undefined
    if (found) return found
  }
  return first
}

export function getContinueWatching(limit: number): Promise<ContinueCard[]> {
  const profileId = readSelectedProfileId()
  if (!readSession() || !profileId) return Promise.resolve<ContinueCard[]>([])

  const path = withLanguage(
    '/api/me/watch-progress/continue?profileId=' + encodeURIComponent(profileId) + '&limit=' + limit,
  )

  return withTimeout(authStatus(path), TIMEOUT_MS, 'me/watch-progress/continue')
    .then((response) => {
      if (profileId !== readSelectedProfileId()) return []
      if (response.status !== 200 || !response.text) return []
      const parsed = JSON.parse(response.text) as ContinueItem[]
      // Bölüm seçimi üçün BÜTÜN sətirlər (yarımçıq, tamamlanmış, eyni serialın hər bölümü).
      rememberResume(profileId, parsed || [])
      if (!parsed || parsed.length === 0) return []

      const cards: ContinueCard[] = []
      /*
        ⚠⚠ EYNİ KONTENT BİR DƏFƏ (2026-08-22-də ekranda ölçüldü): server SERİALIN
           HƏR BÖLÜMÜ üçün ayrıca sətir qaytarır, `contentId` isə hamısında eynidir.
           «Davam et» sırasında «Kuklaxana» ÜÇ, «Mozalan» İKİ dəfə yan-yana
           düşürdü. Android tərəfdə eyni cavab daha pis nəticə verirdi —
           `LazyRow(key = { it.id })` təkrar açarda istisna atır və tətbiq
           AÇILIŞDA çökürdü (həmin gün düzəldildi).
        ⚠ BİRİNCİ NÜSXƏ SAXLANILIR: server sıranı ən son baxışa görə düzür, yəni
          birinci sətir «ən son harada qaldın» deməkdir.
      */
      const seen: Record<string, boolean> = {}
      for (let i = 0; i < parsed.length; i++) {
        const item = parsed[i]
        // ⚠ `slug` OLMADAN kart açıla bilmir (detal marşrutu slug ilə işləyir) —
        //   belə sətri göstərmək «basılan, amma heç nə etməyən kart» olardı.
        if (!item || !item.slug || item.completed) continue
        const key = item.contentId || item.slug
        if (seen[key]) continue
        seen[key] = true
        const duration = item.durationSeconds || 0
        cards.push({
          card: {
            id: item.contentId,
            slug: item.slug,
            title: item.contentTitle || item.slug,
            // ⚠ NÖV cavabda YOXDUR — kart onu yalnız nişan üçün işlədir, ona görə
            //   sabit qalır. Qalan meta isə cavabdadır və fokus qatına ötürülür
            //   (sahib tələbi 2026-08-08: kart qonşu sıralardakı kimi görünsün).
            kind: 'movie',
            // Sıfır «0+» kimi yazılmasın deyə null-a çevrilir (kataloq kartı ilə eyni).
            ageLimit: item.ageLimit ? item.ageLimit : null,
            year: item.year ? item.year : null,
            posterUrl: item.posterUrl,
            rating: typeof item.rating === 'number' ? item.rating : null,
            durationMinutes: item.durationMinutes,
            genreNames: item.genreNames,
            moodNames: item.moodNames,
          },
          progress: duration > 0 ? Math.min(1, Math.max(0, item.positionSeconds / duration)) : 0,
        })
      }
      return cards
    })
    .catch(() => [])
}
